import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
  Skeleton
} from '@bliks-io/ui';
import type { DuplicateCandidate, EntitySuggestionKind } from '@bliks-io/contracts';
import { ENTITY_ICONS } from '@/constants/entityIcons';

export interface DuplicateCandidatesPanelProps {
  candidates: DuplicateCandidate[];
  kind: EntitySuggestionKind;
  onUseExisting: (ref: { source: 'entity' | 'suggestion'; id: string }) => void;
  isChecking?: boolean;
  /** Pending suggestion whose adopt request is in flight. */
  adoptingId?: string;
  disabled?: boolean;
}

export function DuplicateCandidatesPanel({
  candidates,
  kind,
  onUseExisting,
  isChecking,
  adoptingId,
  disabled
}: DuplicateCandidatesPanelProps) {
  const { t } = useTranslation();
  if (!candidates.length) {
    if (!isChecking) return null;
    return (
      <div role="status" className="flex items-center gap-2">
        <Skeleton className="h-4 w-4 shrink-0" />
        <span className="text-muted-foreground text-sm">
          {t('entitySuggestion.duplicates.checking')}
        </span>
      </div>
    );
  }

  const Icon = ENTITY_ICONS[kind];

  return (
    <div className="bg-muted/30 flex flex-col gap-3 rounded-md border p-4">
      <p className="text-foreground text-sm font-medium">
        {t(`entitySuggestion.duplicates.heading.${kind}`)}
      </p>
      <ItemGroup>
        {candidates.map((c) => {
          const isPending = c.source === 'suggestion';
          const descriptionParts = [
            c.preview.secondaryLine,
            ...c.reasons.map((r) => t(`entitySuggestion.duplicates.reasons.${r}`))
          ].filter(Boolean);

          return (
            <Item key={`${c.source}:${c.id}`} variant="default" size="sm">
              <ItemMedia variant="icon">
                <Icon />
              </ItemMedia>
              <ItemContent>
                <ItemTitle>
                  {c.preview.name}
                  {isPending && (
                    <Badge variant="outline" size="sm">
                      {t(`entitySuggestion.${kind}.card.pendingBadge`)}
                    </Badge>
                  )}
                </ItemTitle>
                {descriptionParts.length > 0 && (
                  <ItemDescription>{descriptionParts.join(' · ')}</ItemDescription>
                )}
              </ItemContent>
              <ItemActions>
                <Button
                  variant="link"
                  size="small"
                  type="button"
                  loading={isPending && c.id === adoptingId}
                  disabled={disabled || adoptingId !== undefined}
                  onClick={() => onUseExisting({ source: c.source, id: c.id })}
                >
                  {t(`entitySuggestion.duplicates.useThisInstead.${kind}`)}
                </Button>
              </ItemActions>
            </Item>
          );
        })}
      </ItemGroup>
    </div>
  );
}
